import type { LucideIcon } from "lucide-react";
import { BarChart3, Briefcase, DollarSign, FileText, Loader2, Users } from "lucide-react";
import { Link } from "react-router";
import { Card, PageHeader, StatsCards } from "../../components/ui/patrones";
import { Aviso } from "../../components/ui/primitivos";
import { fetchInicio } from "../../lib/consola-api";
import { numero, pesos } from "../../lib/formato";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { iniciales, resumenDeVentas } from "./inicio";

/**
 * Pantalla de inicio — porteada del `Dashboard` de `imprenta` (ADR 0046,
 * fidelidad visual revisada en ADR 0076).
 *
 * **Se dibuja con lo que manda el worker, no con una lista propia.** Cada
 * tarjeta va detrás del permiso de su dato, así que a un Operario le llegan
 * menos: si la pantalla tuviera las cuatro fijas, mostraría huecos donde el
 * servidor no contestó — y peor, insinuaría que hay un número que no puede ver.
 */

/**
 * El ícono de cada tarjeta, por clave.
 *
 * Vive acá y no en el worker porque es decisión de presentación: el endpoint
 * manda el dato y el permiso, no cómo se dibuja. Son los cuatro de `imprenta`.
 */
const ICONO: Record<string, LucideIcon> = {
  facturacion: DollarSign,
  presupuestos: FileText,
  trabajos_activos: Briefcase,
  clientes_activos: Users,
};

export function InicioPagina() {
  const { me, can } = useSession();
  const datos = useDatos(fetchInicio);

  const tarjetas = datos.datos?.tarjetas ?? [];
  const ventas = datos.datos?.ventasRecientes ?? [];
  const ventasDelMes = datos.datos?.ventasDelMes ?? 0;

  // Las ventas van detrás del mismo permiso que las trae: sin él, el bloque no
  // se muestra vacío — no se muestra.
  const veVentas = can("reportes", "negocio") || can("facturacion", "read");

  return (
    <div className="space-y-6">
      <PageHeader
        titulo={`Hola${me ? `, ${me.nombre.split(" ")[0]}` : ""}`}
        subtitulo="Cómo viene el mes"
      />

      {datos.error ? <Aviso>{datos.error}</Aviso> : null}

      {datos.cargando && !datos.datos ? (
        // El spinner de allá, centrado. Un renglón de texto contra el borde se
        // lee como «no hay nada», no como «está cargando».
        <div className="flex items-center justify-center py-12">
          <Loader2 aria-label="Cargando" className="h-8 w-8 animate-spin text-neutral-400" />
        </div>
      ) : (
        <StatsCards
          estadisticas={tarjetas.map((t) => ({
            titulo: t.titulo,
            valor: t.clave === "facturacion" ? pesos(t.valor) : numero(t.valor),
            ...(ICONO[t.clave] ? { icono: ICONO[t.clave] as LucideIcon } : {}),
            ...(t.detalle ? { descripcion: t.detalle } : {}),
            ...(t.tendencia
              ? { tendencia: { porcentaje: t.tendencia.porcentaje, mejora: t.tendencia.mejora } }
              : {}),
          }))}
        />
      )}

      {veVentas ? (
        <Card className="p-6">
          <h2 className="font-semibold text-neutral-900">Ventas Recientes</h2>
          <p className="mt-1 text-neutral-500 text-sm">{resumenDeVentas(ventasDelMes)}</p>

          {/* El vacío va DENTRO de la tarjeta, como allá: si el bloque entero
              desaparece, no se distingue «no vendiste nada» de «acá no va
              nada». */}
          {ventas.length === 0 ? (
            <p className="py-8 text-center text-neutral-500 text-sm">No hay ventas recientes</p>
          ) : (
            <ul className="mt-6 space-y-6">
              {ventas.map((v) => (
                <li key={v.numero} className="flex items-center gap-4">
                  <span
                    aria-hidden="true"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 font-bold text-neutral-700 text-sm"
                  >
                    {iniciales(v.cliente ?? "")}
                  </span>
                  <div className="min-w-0 space-y-1">
                    <p className="truncate font-medium text-neutral-900 text-sm leading-none">
                      {v.cliente ?? "Sin cliente"}
                    </p>
                    <p className="truncate text-neutral-500 text-sm">
                      {v.numero} · {v.producto}
                    </p>
                  </div>
                  <p className="ml-auto shrink-0 font-medium text-neutral-900 text-sm">
                    {pesos(v.total ?? 0)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      {/* El acceso rápido a Reportes de allá. El otro —«Administración»— es
          plano de plataforma y no tiene equivalente (ADR 0002). */}
      {can("reportes", "negocio") ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card className="p-6 transition-shadow hover:shadow-md">
            <div className="flex items-center gap-3">
              <span className="rounded-lg bg-sky-50 p-2">
                <BarChart3 aria-hidden="true" className="h-6 w-6 text-sky-600" />
              </span>
              <div>
                <h2 className="font-semibold text-base text-neutral-900">Reportes</h2>
                <p className="text-neutral-500 text-sm">Analíticas y métricas</p>
              </div>
            </div>
            <Link
              to="/reportes"
              className="mt-4 inline-block font-medium text-sky-700 text-sm hover:underline"
            >
              Ver reportes →
            </Link>
          </Card>
        </div>
      ) : null}

      {!datos.cargando && tarjetas.length === 0 && !veVentas ? (
        <Card className="p-6">
          <p className="text-neutral-600 text-sm">
            Todavía no hay nada para mostrar acá. Entrá por el menú de la izquierda.
          </p>
        </Card>
      ) : null}
    </div>
  );
}
